import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router-dom";
import { ApiError, createGoal, deleteGoal, fetchGoals } from "../lib/api-client";
import { ErrorAlert } from "../components/Alert";
import { defaultConfigFor, GoalConfigFields } from "../components/GoalConfigFields";
import type { GoalConfig, GoalSource } from "../lib/goal-form-types";
import type { Goal } from "../lib/types";

const SOURCE_LABELS: Record<GoalSource, string> = {
  ga4_event: "GA4イベント連携",
  selector: "セレクタ検知",
  url: "URL到達",
  custom: "カスタム(JS API)",
};

function describeConfig(config: GoalConfig): string {
  switch (config.source) {
    case "ga4_event":
      return `イベント名: ${config.ga4EventName}`;
    case "selector":
      return `セレクタ: ${config.selector}`;
    case "url":
      return `URL: ${config.url} (${config.matchType})`;
    case "custom":
      return `キー: ${config.goalKey}`;
  }
}

export function GoalsPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [config, setConfig] = useState<GoalConfig>(defaultConfigFor("ga4_event"));
  const [isMicro, setIsMicro] = useState(false);
  const [dedup, setDedup] = useState<"session" | "always">("session");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;
    setIsLoading(true);
    fetchGoals(siteId)
      .then(setGoals)
      .catch((err) => setError(err instanceof ApiError ? err.message : "ゴール一覧の取得に失敗しました"))
      .finally(() => setIsLoading(false));
  }, [siteId]);

  function handleSourceChange(source: GoalSource) {
    setConfig(defaultConfigFor(source));
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!siteId) return;
    setCreateError(null);
    setIsCreating(true);
    try {
      const goal = await createGoal(siteId, { name, config, isMicro, dedup });
      setGoals((prev) => [...prev, goal]);
      setName("");
      setConfig(defaultConfigFor("ga4_event"));
      setIsMicro(false);
      setDedup("session");
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "ゴールの作成に失敗しました");
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDelete(goalId: string) {
    try {
      await deleteGoal(goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ゴールの削除に失敗しました");
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold mb-1">ゴール一覧</h1>
        <p className="text-sm text-gray-500 mb-4">
          ヒートマップのCVセグメントとABテストのゴールは、ここで登録した定義を共有します。
        </p>
        <ErrorAlert message={error} />
        {isLoading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : goals.length === 0 ? (
          <p className="text-sm text-gray-500">ゴールがまだ登録されていません</p>
        ) : (
          <table className="w-full bg-white border border-gray-200 rounded-lg text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-4 py-2 font-medium">名前</th>
                <th className="px-4 py-2 font-medium">検知方式</th>
                <th className="px-4 py-2 font-medium">設定</th>
                <th className="px-4 py-2 font-medium">種別</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {goals.map((goal) => (
                <tr key={goal.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2">{goal.name}</td>
                  <td className="px-4 py-2">{SOURCE_LABELS[goal.source]}</td>
                  <td className="px-4 py-2 font-mono text-xs">{describeConfig(goal.config)}</td>
                  <td className="px-4 py-2">{goal.isMicro ? "マイクロCV" : "主要CV"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(goal.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-md">
        <h2 className="text-base font-semibold mb-3">ゴール登録</h2>
        <ErrorAlert message={createError} />
        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="goal-name">
              ゴール名
            </label>
            <input
              id="goal-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: LINE登録タップ"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="goal-source">
              検知方式
            </label>
            <select
              id="goal-source"
              value={config.source}
              onChange={(e) => handleSourceChange(e.target.value as GoalSource)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="ga4_event">GA4イベント連携（優先・設定手間ゼロ）</option>
              <option value="selector">セレクタ検知（GTM未導入向け）</option>
              <option value="url">URL到達</option>
              <option value="custom">カスタム(JS API)</option>
            </select>
          </div>

          <GoalConfigFields config={config} onChange={setConfig} />

          <div>
            <label className="block text-sm text-gray-700 mb-1" htmlFor="goal-dedup">
              集計時のユニーク化
            </label>
            <select
              id="goal-dedup"
              value={dedup}
              onChange={(e) => setDedup(e.target.value as "session" | "always")}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="session">1セッション1回のみカウント</option>
              <option value="always">発生の都度カウント</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isMicro}
              onChange={(e) => setIsMicro(e.target.checked)}
              className="rounded border-gray-300"
            />
            マイクロCV（資料DL・スクロール完了等の段階指標）として登録する
          </label>

          <button
            type="submit"
            disabled={isCreating}
            className="rounded bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isCreating ? "作成中..." : "ゴールを作成"}
          </button>
        </form>
      </div>
    </div>
  );
}
